/**
 * GTranslate セレクトの挙動カスタム
 * - English（独立リンク）
 *   ・採用・インタビュー日本語 → /en/ トップ（Google翻訳は使わない）
 *   ・その他の日本語ページ → 対訳の /en/**
 * - 日本語 → /** （日本語サイト、Google翻訳解除）
 * - その他 → Google翻訳
 *   /en にいる場合は /** へ遷移してから Google翻訳を適用
 */
class GTranslateRouter {
  constructor() {
    this.bound = new WeakSet();
    this.enLinksBound = new WeakSet();
    this.translateToken = 0;
    this.trimming = false;
    this.init();
  }

  init() {
    // 日本語へ戻した直後の再適用を先に潰す
    this.enforceJapaneseIfNeeded();

    if (this.isEnSite()) {
      this.clearTranslation();
    }

    this.trimLanguageOptions();
    this.bindSelectors();
    this.bindEnglishLinks();
    this.observeSelectors();
    this.watchLanguageLabel();
    this.applyPendingTranslate();
  }

  isEnSite() {
    const path = location.pathname;
    return path === '/en' || path === '/en/' || path.startsWith('/en/');
  }

  /**
   * 採用・インタビューの日本語ページ
   * → English は対訳ではなく /en/ トップへ
   */
  isRecruitJaPage() {
    if (this.isEnSite()) return false;
    const path = location.pathname.replace(/\/+$/, '') || '/';
    return (
      path === '/graduate-recruitment' ||
      path.startsWith('/graduate-recruitment/') ||
      path === '/career' ||
      path.startsWith('/career/') ||
      path === '/interview' ||
      path.startsWith('/interview/')
    );
  }

  enHomePath() {
    return '/en/';
  }

  pathWithoutEn() {
    const path = location.pathname;
    if (path === '/en' || path === '/en/') return '/';
    if (path.startsWith('/en/')) {
      const stripped = path.replace(/^\/en/, '');
      return stripped || '/';
    }
    return path;
  }

  pathWithEn() {
    const base = this.pathWithoutEn();
    if (base === '/') return '/en/';
    return '/en' + (base.endsWith('/') ? base : base + '/');
  }

  /**
   * JP / EN は外のトグルにあるので、プルダウンは中国語2つと韓国語だけにする。
   * 英語サイトのPCでは未選択時の表示を Language にする。
   */
  trimLanguageOptions() {
    document.querySelectorAll('.gt_selector').forEach((select) => {
      select.querySelectorAll('option').forEach((opt) => {
        const value = opt.getAttribute('value') || '';
        if (value.endsWith('|en') || value.endsWith('|ja')) {
          opt.remove();
        }
      });
      if (select.value.endsWith('|en') || select.value.endsWith('|ja')) {
        select.value = '';
      }
    });
    this.labelLanguageSelects();
  }

  isPc() {
    return window.matchMedia('(min-width: 768px)').matches;
  }

  labelLanguageSelects() {
    const label = this.isEnSite() && this.isPc() ? 'Language' : '言語を選択';
    document.querySelectorAll('.gt_selector').forEach((select) => {
      const empty = select.querySelector('option[value=""]');
      if (empty && empty.textContent !== label) empty.textContent = label;
      if (select.getAttribute('aria-label') !== label) {
        select.setAttribute('aria-label', label);
      }
    });
  }

  watchLanguageLabel() {
    const mq = window.matchMedia('(min-width: 768px)');
    const onChange = () => this.labelLanguageSelects();
    if (typeof mq.addEventListener === 'function') {
      mq.addEventListener('change', onChange);
    } else if (typeof mq.addListener === 'function') {
      mq.addListener(onChange);
    }
  }

  /**
   * English / JP 独立リンク
   * Google翻訳は使わず、テーマ側の日英URLへ遷移する
   */
  bindEnglishLinks() {
    document.querySelectorAll('.ns-gt-nav__link, .ns-gt-nav__en').forEach((link) => {
      if (this.enLinksBound.has(link)) return;
      this.enLinksBound.add(link);
      link.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopImmediatePropagation();
        this.abortPendingTranslate();
        this.clearTranslation();
        sessionStorage.removeItem('ns_gt_pending');
        sessionStorage.removeItem('ns_gt_force_ja');

        const lang = link.getAttribute('data-ns-lang') || 'en';
        const href = link.getAttribute('href');

        if (lang === 'ja') {
          sessionStorage.setItem('ns_gt_force_ja', '1');
          this.setJapaneseLangStorage();
          location.href = href || this.pathWithoutEn();
          return;
        }

        // English: 採用系は必ず /en/ トップ、それ以外は href（対訳）優先
        if (this.isRecruitJaPage()) {
          location.href = this.enHomePath();
          return;
        }
        location.href = href || this.pathWithEn();
      });
    });
  }

  clearGoogTrans() {
    const expire = 'Thu, 01 Jan 1970 00:00:00 GMT';
    const host = location.hostname;
    const domains = ['', host];
    const parts = host.split('.');
    // example.com / .example.com（localhost の domain= は無効なので付けない）
    if (parts.length >= 2 && parts[parts.length - 1] !== 'localhost') {
      domains.push('.' + parts.slice(-2).join('.'));
      domains.push('.' + host);
    }
    const paths = ['/', location.pathname, location.pathname.replace(/\/+$/, '') || '/'];
    const extras = ['', ';SameSite=Lax', ';SameSite=None;Secure', ';Secure'];
    paths.forEach((p) => {
      domains.forEach((domain) => {
        const domainPart = domain ? `;domain=${domain}` : '';
        extras.forEach((extra) => {
          document.cookie = `googtrans=;expires=${expire};path=${p}${domainPart}${extra}`;
        });
      });
    });
  }

  /**
   * GTranslate は url_structure=none のとき
   * localStorage.__GT_TRANSLATE_LANGS に言語を保持する。
   * クッキーだけ消すとリロード後に翻訳言語へ戻る。
   */
  clearGtLangStorage() {
    try {
      localStorage.removeItem('__GT_TRANSLATE_LANGS');
    } catch (e) {
      // ignore
    }
  }

  /**
   * 日本語を明示保存する。
   * removeItem だけだと、lib 初期化の競合で前言語が書き戻されることがある。
   */
  setJapaneseLangStorage() {
    try {
      localStorage.setItem(
        '__GT_TRANSLATE_LANGS',
        JSON.stringify({ srcLang: 'ja', tgtLang: 'ja' })
      );
    } catch (e) {
      // ignore
    }
  }

  clearTranslation() {
    this.clearGoogTrans();
    this.clearGtLangStorage();
    sessionStorage.removeItem('ns_gt_pending');
  }

  abortPendingTranslate() {
    this.translateToken += 1;
  }

  syncSelectors(langPair) {
    document.querySelectorAll('.gt_selector').forEach((select) => {
      select.value = langPair;
    });
  }

  /**
   * GTranslate lib 経由で原文に戻す（可能なら）
   * translate('ja','ja') は restore + localStorage クリアを行う
   */
  revertGoogleTranslate() {
    if (typeof window.doGTranslate === 'function') {
      try {
        window.doGTranslate('ja|ja');
      } catch (e) {
        // ignore
      }
    }
    if (window.__GT && window.__GT.translator && typeof window.__GT.translator.revert === 'function') {
      try {
        window.__GT.translator.revert();
      } catch (e) {
        // ignore
      }
    }
  }

  /**
   * 日本語選択後に中国語等が復活しないよう、リロード後も再アサートする
   */
  enforceJapaneseIfNeeded() {
    if (sessionStorage.getItem('ns_gt_force_ja') !== '1') return;

    sessionStorage.removeItem('ns_gt_force_ja');
    this.abortPendingTranslate();
    sessionStorage.removeItem('ns_gt_pending');

    const assertJa = () => {
      this.clearGoogTrans();
      this.setJapaneseLangStorage();
      this.revertGoogleTranslate();
      this.setJapaneseLangStorage();
      this.syncSelectors('');
      if (document.documentElement.lang && document.documentElement.lang !== 'ja') {
        document.documentElement.lang = 'ja';
      }
    };

    assertJa();
    setTimeout(assertJa, 100);
    setTimeout(assertJa, 500);
    setTimeout(assertJa, 1500);
  }

  hasActiveTranslation() {
    if (/(?:^|; )googtrans=/.test(document.cookie)) {
      const m = document.cookie.match(/(?:^|; )googtrans=([^;]*)/);
      if (m && m[1] && m[1] !== '/ja/ja' && m[1] !== '') return true;
    }
    try {
      const stored = JSON.parse(localStorage.getItem('__GT_TRANSLATE_LANGS'));
      if (stored && typeof stored.tgtLang === 'string' && stored.tgtLang !== 'ja') {
        return true;
      }
    } catch (e) {
      // ignore
    }
    return false;
  }

  setGoogTrans(lang) {
    const value = `/ja/${lang}`;
    document.cookie = `googtrans=${value};path=/`;
    if (location.hostname.indexOf('.') !== -1) {
      document.cookie = `googtrans=${value};path=/;domain=${location.hostname}`;
    }
  }

  navigate(path) {
    location.href = path + location.search + location.hash;
  }

  hardReloadSamePage() {
    // bfcache 回避のため replace でフル遷移
    const url = location.pathname + location.search + location.hash;
    location.replace(url);
  }

  bindSelectors() {
    document.querySelectorAll('.gt_selector').forEach((select) => {
      if (this.bound.has(select)) return;
      this.bound.add(select);
      // capture で GTranslate 本体より先に処理する
      select.addEventListener('change', (e) => this.onChange(e), true);
    });
  }

  observeSelectors() {
    const observe = () => {
      this.selectorObserver.observe(document.documentElement, {
        childList: true,
        subtree: true,
      });
    };
    this.selectorObserver = new MutationObserver(() => {
      if (this.trimming) return;
      this.trimming = true;
      this.selectorObserver.disconnect();
      this.trimLanguageOptions();
      this.bindSelectors();
      this.bindEnglishLinks();
      this.trimming = false;
      observe();
    });
    observe();
  }

  applyGoogleTranslate(lang) {
    const token = ++this.translateToken;

    // 切替前に前回の翻訳状態を消す（ko→ja→zh などで残留しないように）
    this.clearGoogTrans();
    this.setGoogTrans(lang);
    try {
      localStorage.setItem(
        '__GT_TRANSLATE_LANGS',
        JSON.stringify({ srcLang: 'ja', tgtLang: lang })
      );
    } catch (e) {
      // ignore
    }

    const tryApply = () => {
      if (token !== this.translateToken) return;
      if (typeof window.doGTranslate === 'function') {
        window.doGTranslate('ja|' + lang);
        this.syncSelectors('ja|' + lang);
        return;
      }
      setTimeout(tryApply, 200);
    };
    tryApply();
  }

  onChange(e) {
    const select = e.target;
    if (!(select instanceof HTMLSelectElement)) return;
    if (!select.classList.contains('gt_selector')) return;

    const value = select.value;
    if (!value || value.indexOf('|') === -1) return;

    const targetLang = value.split('|')[1];

    // English（プルダウンに残っていた場合の保険）→ Google翻訳は使わない
    if (targetLang === 'en') {
      e.preventDefault();
      e.stopImmediatePropagation();
      this.abortPendingTranslate();
      this.clearTranslation();
      sessionStorage.removeItem('ns_gt_force_ja');
      location.href = this.isRecruitJaPage() ? this.enHomePath() : this.pathWithEn();
      return;
    }

    // 日本語 → 日本語サイトへ（翻訳解除）
    // ※ clear だけだと DOM / cookie / storage の競合で中国語等へ戻ることがある
    if (targetLang === 'ja') {
      e.preventDefault();
      e.stopImmediatePropagation();
      this.abortPendingTranslate();
      sessionStorage.removeItem('ns_gt_pending');
      sessionStorage.setItem('ns_gt_force_ja', '1');

      // 可能なら GTranslate 自身の restore を先に走らせる
      this.revertGoogleTranslate();
      this.clearGoogTrans();
      this.setJapaneseLangStorage();
      this.syncSelectors('');

      if (this.isEnSite()) {
        this.navigate(this.pathWithoutEn());
      } else {
        this.hardReloadSamePage();
      }
      return;
    }

    // その他 → Google翻訳
    // /en にいるときは日本語URLへ移してから翻訳
    if (this.isEnSite()) {
      e.preventDefault();
      e.stopImmediatePropagation();
      this.abortPendingTranslate();
      this.clearTranslation();
      sessionStorage.removeItem('ns_gt_force_ja');
      this.setGoogTrans(targetLang);
      sessionStorage.setItem('ns_gt_pending', targetLang);
      this.navigate(this.pathWithoutEn());
      return;
    }

    // 日本語サイト上の他言語も明示的に適用
    // （採用ページで EN 翻訳後→他ページ→韓国語、などの連続切替で残留しないように）
    e.preventDefault();
    e.stopImmediatePropagation();
    sessionStorage.removeItem('ns_gt_pending');
    sessionStorage.removeItem('ns_gt_force_ja');
    this.applyGoogleTranslate(targetLang);
  }

  applyPendingTranslate() {
    if (this.isEnSite()) return;
    if (sessionStorage.getItem('ns_gt_force_ja') === '1') return;

    const pending = sessionStorage.getItem('ns_gt_pending');
    if (!pending) return;

    sessionStorage.removeItem('ns_gt_pending');
    this.applyGoogleTranslate(pending);
  }
}
